import { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { ConfirmDialog } from './ConfirmDialog';
import { Input } from './Input';
import { Button } from './Button';
import { MapPin, Plus, ChevronDown, Search, Check, X as XIcon } from 'lucide-react';
import clsx from 'clsx';
import type { AdminCoreVenue, AdminCoreVenueUpdate, Address, MonthPeriod, Month, VenueStatus } from '../../types';
import { cleanAddress, emptyAddress } from '../../utils/addressFormat';

interface CoreVenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (venueId: string, data: AdminCoreVenueUpdate) => Promise<void>;
  venue: AdminCoreVenue | null;
  /** When set, shows merge UI instead of normal update */
  mergeSourceNames?: string[];
  onMerge?: (venueId: string, data: AdminCoreVenueUpdate, hasChanges: boolean) => Promise<void>;
}

const venueTypes = [
  { value: 'concert_hall', label: 'Concert Hall' },
  { value: 'bar', label: 'Bar' },
  { value: 'concert_bar', label: 'Concert Bar' },
  { value: 'jazz_club', label: 'Jazz Club' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'city_concert_hall', label: 'City Concert Hall' },
  { value: 'festival', label: 'Festival' },
  { value: 'association', label: 'Association' },
  { value: 'city', label: 'City' },
  { value: 'comite_des_fetes', label: 'Comité des fêtes' },
  { value: 'competition', label: 'Competition' },
  { value: 'mentoring', label: 'Mentoring' },
  { value: 'booking_agency', label: 'Booking Agency' },
  { value: 'other', label: 'Other' },
] as const;

const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'no_concerts', label: 'No Concerts' },
  { value: 'unknown', label: 'Unknown' },
] as const;

const musicStyles = [
  'Rock', 'Jazz', 'Blues', 'Folk', 'Classical', 'Pop',
  'Electronic', 'Metal', 'Punk', 'Hip Hop', 'Country', 'Reggae',
];

const MONTHS: { value: Month; label: string; short: string }[] = [
  { value: 'january', label: 'January', short: 'Jan' },
  { value: 'february', label: 'February', short: 'Feb' },
  { value: 'march', label: 'March', short: 'Mar' },
  { value: 'april', label: 'April', short: 'Apr' },
  { value: 'may', label: 'May', short: 'May' },
  { value: 'june', label: 'June', short: 'Jun' },
  { value: 'july', label: 'July', short: 'Jul' },
  { value: 'august', label: 'August', short: 'Aug' },
  { value: 'september', label: 'September', short: 'Sep' },
  { value: 'october', label: 'October', short: 'Oct' },
  { value: 'november', label: 'November', short: 'Nov' },
  { value: 'december', label: 'December', short: 'Dec' },
];

const emptyMonthPeriod = (): MonthPeriod => ({ all_year: false, months: [] });

interface CoreVenueFormData {
  name: string;
  url: string;
  type: AdminCoreVenueUpdate['type'];
  address: Address;
  styles: string[];
  previous_artists: string[];
  status: VenueStatus;
  concert_period: MonthPeriod;
}

export function CoreVenueModal({ isOpen, onClose, onSubmit, venue, mergeSourceNames, onMerge }: CoreVenueModalProps) {
  const [formData, setFormData] = useState<CoreVenueFormData>({
    name: '',
    url: '',
    type: 'bar',
    address: emptyAddress(),
    styles: [],
    previous_artists: [],
    status: 'open',
    concert_period: emptyMonthPeriod(),
  });
  const [newStyle, setNewStyle] = useState('');
  const [newArtist, setNewArtist] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);
  const initialFormDataRef = useRef<string>('');
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [error, setError] = useState('');
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
        setTypeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && venue) {
      const initial: CoreVenueFormData = {
        name: venue.name || '',
        url: venue.url || '',
        type: venue.type || 'bar',
        address: venue.address || emptyAddress(),
        styles: venue.styles || [],
        previous_artists: venue.previous_artists || [],
        status: venue.status || 'open',
        concert_period: venue.concert_period || emptyMonthPeriod(),
      };
      setFormData(initial);
      initialFormDataRef.current = JSON.stringify(initial);
      setError('');
      setNewStyle('');
      setNewArtist('');
      setShowAcceptConfirm(false);
      setShowRevokeConfirm(false);
    }
  }, [isOpen, venue]);

  const handleSubmit = async () => {
    if (!venue) return;
    setError('');

    if (!formData.name.trim()) {
      setError('Venue name is required');
      return;
    }

    const data: AdminCoreVenueUpdate = {
      name: formData.name,
      url: formData.url || undefined,
      type: formData.type,
      address: cleanAddress(formData.address),
      styles: formData.styles,
      previous_artists: formData.previous_artists,
      status: formData.status,
      concert_period: formData.concert_period,
    };

    try {
      setIsSubmitting(true);
      await onSubmit(venue.id, data);
      onClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.detail || 'Failed to update venue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasFormChanges = JSON.stringify(formData) !== initialFormDataRef.current;

  const handleMerge = async () => {
    if (!venue || !onMerge) return;
    setError('');

    if (!formData.name.trim()) {
      setError('Venue name is required');
      return;
    }

    const data: AdminCoreVenueUpdate = {
      name: formData.name,
      url: formData.url || undefined,
      type: formData.type,
      address: cleanAddress(formData.address),
      styles: formData.styles,
      previous_artists: formData.previous_artists,
      status: formData.status,
      concert_period: formData.concert_period,
    };

    try {
      setIsMerging(true);
      await onMerge(venue.id, data, hasFormChanges);
      onClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.detail || err.message || 'Failed to merge venues');
    } finally {
      setIsMerging(false);
    }
  };

  const handleVisibilityChange = async (newVisibility: 'public' | 'refused_public') => {
    if (!venue) return;
    try {
      setIsChangingVisibility(true);
      await onSubmit(venue.id, { visibility: newVisibility });
      setShowAcceptConfirm(false);
      setShowRevokeConfirm(false);
      onClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.detail || 'Failed to change visibility');
    } finally {
      setIsChangingVisibility(false);
    }
  };

  const handleAddStyle = (style: string) => {
    if (style && !formData.styles.includes(style)) {
      setFormData({ ...formData, styles: [...formData.styles, style] });
      setNewStyle('');
    }
  };

  const handleRemoveStyle = (style: string) => {
    setFormData({ ...formData, styles: formData.styles.filter((s) => s !== style) });
  };

  const handleAddArtist = (artist: string) => {
    if (artist && !formData.previous_artists.includes(artist)) {
      setFormData({ ...formData, previous_artists: [...formData.previous_artists, artist] });
      setNewArtist('');
    }
  };

  const handleRemoveArtist = (artist: string) => {
    setFormData({ ...formData, previous_artists: formData.previous_artists.filter((a) => a !== artist) });
  };

  const updateAddressField = (field: keyof Address, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value || undefined },
    }));
  };

  const toggleMonth = (month: Month) => {
    const current = formData.concert_period;
    const months = current.months.includes(month)
      ? current.months.filter(m => m !== month)
      : [...current.months, month];
    setFormData(prev => ({ ...prev, concert_period: { months, all_year: false } }));
  };

  const toggleAllYear = () => {
    const current = formData.concert_period;
    setFormData(prev => ({
      ...prev,
      concert_period: { all_year: !current.all_year, months: [] },
    }));
  };

  const canAccept = venue?.visibility === 'pending_public' || venue?.visibility === 'refused_public';
  const canRevoke = venue?.visibility === 'public' || venue?.visibility === 'pending_public';

  if (!isOpen || !venue) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={mergeSourceNames ? 'Merge Venues' : 'Edit Core Venue'}
        size="4xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting || isMerging}>
              Cancel
            </Button>
            <div className="flex items-center gap-3">
              {!mergeSourceNames && canRevoke && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowRevokeConfirm(true)}
                  disabled={isSubmitting}
                >
                  <XIcon className="w-3.5 h-3.5" />
                  Refuse
                </Button>
              )}
              {!mergeSourceNames && canAccept && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAcceptConfirm(true)}
                  disabled={isSubmitting}
                >
                  <Check className="w-3.5 h-3.5" />
                  Accept Public
                </Button>
              )}
              {mergeSourceNames ? (
                <Button
                  type="button"
                  variant="primary"
                  isLoading={isMerging}
                  onClick={handleMerge}
                >
                  {hasFormChanges ? 'Update & Merge' : 'Merge'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  isLoading={isSubmitting}
                  onClick={handleSubmit}
                >
                  Update Venue
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Merge info */}
          {mergeSourceNames && mergeSourceNames.length > 0 && (
            <div className="p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg text-amber-300 text-sm">
              <span className="font-medium">Merging {mergeSourceNames.length} venue{mergeSourceNames.length > 1 ? 's' : ''} into this one:</span>
              <span className="ml-1 text-amber-400">{mergeSourceNames.join(', ')}</span>
            </div>
          )}

          {/* Owner info */}
          <div className="flex items-center gap-2 text-xs text-dark-text-muted">
            <span>Owner: <span className="text-dark-text-secondary">{venue.owner_email || venue.owner_user_id}</span></span>
            <span className="mx-1">|</span>
            <span>Visibility: <span className={clsx(
              'font-medium',
              venue.visibility === 'public' && 'text-emerald-400',
              venue.visibility === 'pending_public' && 'text-amber-400',
              venue.visibility === 'refused_public' && 'text-red-400',
            )}>{venue.visibility.replace('_', ' ')}</span></span>
          </div>

          {/* Row 1: Name | Status | Type */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-3">
            <Input
              label="Venue Name *"
              type="text"
              placeholder="e.g., The Blue Note"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as CoreVenueFormData['status'] })}
                className="input"
              >
                {statusOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div ref={typeDropdownRef} className="relative min-w-[160px]">
              <label className="block text-sm font-medium text-dark-text-secondary mb-1">Type *</label>
              <button
                type="button"
                onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setTypeSearch(''); }}
                className="w-full flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm font-medium text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              >
                <span>{venueTypes.find(t => t.value === formData.type)?.label || 'Select type'}</span>
                <ChevronDown className={clsx('w-4 h-4 ml-2 text-dark-text-secondary transition-transform', typeDropdownOpen && 'rotate-180')} />
              </button>
              {typeDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-dark-border">
                    <Search className="w-3.5 h-3.5 text-dark-text-muted" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={typeSearch}
                      onChange={(e) => setTypeSearch(e.target.value)}
                      className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {venueTypes
                      .filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase()))
                      .map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => { setFormData({ ...formData, type: value }); setTypeDropdownOpen(false); setTypeSearch(''); }}
                          className={clsx(
                            'w-full text-left px-3 py-2 text-sm',
                            formData.type === value
                              ? 'bg-primary-900/30 text-primary-400'
                              : 'text-dark-text-secondary hover:bg-dark-border'
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    {venueTypes.filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-dark-text-muted text-center">No matching types</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* URL */}
          <Input
            label="Website URL"
            type="url"
            placeholder="https://venue-website.com"
            value={formData.url || ''}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          />

          {/* Music Styles | Previous Artists */}
          <div className="border-t border-dark-border pt-3 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">Music Styles</label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="text"
                  placeholder="Custom style..."
                  value={newStyle}
                  onChange={(e) => setNewStyle(e.target.value)}
                />
                <Button type="button" variant="secondary" size="sm" onClick={() => handleAddStyle(newStyle)} disabled={!newStyle.trim()}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {formData.styles.filter(s => !musicStyles.includes(s)).length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {formData.styles.filter(s => !musicStyles.includes(s)).map((style) => (
                    <span key={style} className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium">
                      {style}
                      <button type="button" onClick={() => handleRemoveStyle(style)}>x</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {musicStyles.map((style) => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => handleAddStyle(style)}
                    disabled={formData.styles.includes(style)}
                    className={clsx(
                      'px-2.5 py-1 rounded-full text-xs font-medium',
                      formData.styles.includes(style)
                        ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                        : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                    )}
                  >
                    {formData.styles.includes(style) ? '+ ' : '+ '}{style}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-text-secondary mb-2">Previous Artists</label>
              <div className="flex gap-2 mb-2">
                <Input type="text" placeholder="Add artist..." value={newArtist} onChange={(e) => setNewArtist(e.target.value)} />
                <Button type="button" variant="secondary" size="sm" onClick={() => handleAddArtist(newArtist)} disabled={!newArtist.trim()}>
                  <Plus className="w-3.5 h-3.5" /> Add
                </Button>
              </div>
              {formData.previous_artists.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {formData.previous_artists.map((artist) => (
                    <span key={artist} className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium">
                      {artist}
                      <button type="button" onClick={() => handleRemoveArtist(artist)}>x</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="border-t border-dark-border pt-3">
            <label className="block text-sm font-medium text-dark-text-secondary mb-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Address
            </label>
            <div className="grid grid-cols-3 gap-2">
              <Input label="Street" placeholder="123 Main St" value={formData.address?.street || ''} onChange={(e) => updateAddressField('street', e.target.value)} />
              <Input label="City" placeholder="Paris" value={formData.address?.city || ''} onChange={(e) => updateAddressField('city', e.target.value)} />
              <Input label="Postal Code" placeholder="75001" value={formData.address?.zip_code || ''} onChange={(e) => updateAddressField('zip_code', e.target.value)} />
              <Input label="Dept/County" placeholder="Seine" value={formData.address?.administrative_region_1 || ''} onChange={(e) => updateAddressField('administrative_region_1', e.target.value)} />
              <Input label="Region/State" placeholder="Ile-de-France" value={formData.address?.administrative_region_2 || ''} onChange={(e) => updateAddressField('administrative_region_2', e.target.value)} />
              <Input label="Country" placeholder="France" value={formData.address?.country || ''} onChange={(e) => updateAddressField('country', e.target.value)} />
            </div>
            {formData.address?.unparsed_address && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-dark-text-secondary mb-1">Full Address</label>
                <div className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text-muted text-sm min-h-[38px]">
                  {formData.address.unparsed_address}
                </div>
              </div>
            )}
          </div>

          {/* Concert Period */}
          <div className="border-t border-dark-border pt-3">
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Concert Period</label>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={toggleAllYear}
                className={clsx(
                  'px-2 py-0.5 rounded-sm text-xs font-medium border',
                  formData.concert_period.all_year
                    ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                    : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                )}
              >
                All
              </button>
              {MONTHS.map(({ value, short }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => toggleMonth(value)}
                  disabled={formData.concert_period.all_year}
                  className={clsx(
                    'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
                    formData.concert_period.all_year
                      ? 'border-dark-border bg-dark-bg text-dark-text-muted cursor-not-allowed'
                      : formData.concert_period.months.includes(value)
                      ? 'border-primary-600 bg-primary-900/30 text-primary-400'
                      : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
                  )}
                >
                  {short}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showAcceptConfirm}
        onClose={() => setShowAcceptConfirm(false)}
        onConfirm={() => handleVisibilityChange('public')}
        title="Accept Public Venue"
        message="This will make this venue visible to all users. Continue?"
        confirmLabel="Accept"
        isLoading={isChangingVisibility}
      />
      <ConfirmDialog
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={() => handleVisibilityChange('refused_public')}
        title="Refuse Public Venue"
        message="This will mark this venue as refused. The owner will be notified. Continue?"
        confirmLabel="Refuse"
        danger
        isLoading={isChangingVisibility}
      />
    </>
  );
}
